
import {getLogger} from 'loglevel';
import {RequestPipeline} from './RequestPipeline';
import {
  jQueryRequestFetchMiddleware,
  jsonpRequestFetchMiddleware,
  hybrisRequestFetchMiddleware } from './requestFetchMiddleware';
import {storageHandlers} from './requestStorageHandlers';
import {expiryHandlers} from './requestExpiryHandlers';
import cookies from 'js-cookie';
import * as featureFlags from '../utilities/featureFlags';

export const log = getLogger('RequestPipeline');

/**
 * Make a request to a server URL using the
 * request pipeline
 *
 * See conceptual documentation here:
 * https://arrowecommerce.atlassian.net/wiki/display/ARW/Request+Pipelines
 *
 * Middleware should resolve when complete and reject with an Error
 *
 * @param requestOptions
 * @param requestOptions.url
 * @param requestOptions.storageOptions.enabled [boolean]
 * @param requestOptions.storageOptions.expiryHandlerKey [EXPIRY_HANDLER_KEY]
 * @param requestOptions.storageOptions.storageHandlerKey [STORAGE_HANDLER_KEY]
 * @param requestOptions.retryOptions.enabled [boolean]
 * @param requestOptions.retryOptions.attempts [number]
 *
 * @returns RequestPipeline
 */
export function makeRequest(requestOptions) {
  const request = new RequestPipeline(requestOptions);
  const storageOptions = request.storageOptions;
  log.trace(`Starting request for ${requestOptions.url}`, requestOptions);
  if (storageOptions.enabled) {
    request.use(retrieveCacheRequestMiddleware);
    request.use(checkExpiredCacheRequestMiddleware);
  }
  if (request.requestOptions.dataType === 'jsonp') {
    request.use(jsonpRequestFetchMiddleware);
  } else {
    if (request.requestOptions.hybrisApi) {
      request.use(hybrisRequestFetchMiddleware);
    } else {
      request.use(jQueryRequestFetchMiddleware);
    }
  }
  if (storageOptions.enabled) request.use(cacheResponseMiddleware);
  return request;
}

export function runRequest(requestOptions) {
  return makeRequest(requestOptions).run();
}

export function hybrisMakeRequest(options) {
  options.hybrisApi = true;
  return makeRequest(options);
}


function retrieveCacheRequestMiddleware(request) {
  let storageOptions = request.storageOptions;
  let storageHandler = storageHandlers[storageOptions.storageHandlerKey];
  if (storageHandler) {
    return Promise
      .resolve(storageHandler.retrieveCache(request))
      .then((cachedData) => {
        request.loadState(cachedData);
      });
  }
}

function checkExpiredCacheRequestMiddleware(request) {
  const storageOptions = request.storageOptions;
  // Return quickly
  if (!storageOptions) return;
  if (storageOptions && !storageOptions.enabled) return;
  // use the handlers
  const storageHandler = storageHandlers[storageOptions.storageHandlerKey];
  const expiryHandler = expiryHandlers[storageOptions.expiryHandlerKey];
  if (storageHandler && expiryHandler) {
    if (expiryHandler && expiryHandler.isExpired(request)) {
      log.trace(`Cache expired for ${request.requestId}`);
      return Promise
        .resolve(storageHandler.clearCache(request))
        .then(() => {
          request.response = null;
        });
    }
  }
}

function cacheResponseMiddleware(request) {
  const storageOptions = request.storageOptions;
  let storageHandler = storageHandlers[storageOptions.storageHandlerKey];
  const expiryHandler = expiryHandlers[storageOptions.expiryHandlerKey];
  let shouldCacheResponse = storageOptions.enabled && !request.meta.cacheTimestamp;
  if (!(storageHandler && expiryHandler)) shouldCacheResponse = false;
  if (!request.response) shouldCacheResponse = false;
  if (request.error) shouldCacheResponse = false;
  if (shouldCacheResponse) {
    expiryHandler.setMeta(request);
    return Promise
      .resolve(storageHandler.saveCache(request))
      .then(() => {
        log.trace(`Cached to storage for ${request.requestId}`);
      });
  }
}




// WEBPACK FOOTER //
// ./assets/utilities/makeRequest.js